import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { ArrowLeft, Upload, Calendar, MapPin, Globe, Image as ImageIcon, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CreateExhibitionPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    cover_image: '',
    organizer: '',
    location: '',
    start_date: '',
    end_date: '',
    is_online: true
  });

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try {
      const res = await axios.post(`${API}/upload/image`, formDataUpload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setFormData({ ...formData, cover_image: res.data.url });
      toast.success('封面上传成功');
    } catch (err) {
      toast.error('上传失败');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error('请输入展览标题');
      return;
    }
    
    setLoading(true);
    try {
      // 准备提交数据，将空字符串转为null
      const submitData = {
        ...formData,
        start_date: formData.start_date || null,
        end_date: formData.end_date || null,
        organizer: formData.organizer || null,
        location: formData.location || null,
        description: formData.description || null,
        cover_image: formData.cover_image || null
      };
      
      const res = await axios.post(`${API}/exhibitions/submit`, submitData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('展览创建成功！');
      navigate(`/exhibitions/${res.data.id}`);
    } catch (err) {
      console.error('Create exhibition error:', err);
      toast.error(err.response?.data?.detail || '创建失败');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-amber-50/50 pt-16 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <h2 className="text-xl font-semibold mb-4">请先登录</h2>
            <p className="text-amber-600 mb-6">创建展览需要登录账号</p>
            <Button onClick={() => navigate('/login')}>去登录</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-amber-50/50 pt-16">
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* 返回按钮 */}
        <button 
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-amber-700/80 hover:text-amber-900 mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          返回
        </button>

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">创建展览</CardTitle>
            <CardDescription>
              发布您的艺术展览，与更多人分享艺术之美
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* 封面图片 */}
              <div>
                <Label>展览封面</Label>
                <div 
                  onClick={() => fileInputRef.current?.click()}
                  className="mt-2 border-2 border-dashed border-amber-300 rounded-xl p-8 text-center cursor-pointer hover:border-gray-400 transition-colors"
                >
                  {formData.cover_image ? (
                    <div className="relative">
                      <img loading="lazy"
                        src={formData.cover_image} 
                        alt="封面预览" 
                        className="max-h-48 mx-auto rounded-lg object-cover"
                      />
                      <p className="text-sm text-amber-600 mt-2">点击更换封面</p>
                    </div>
                  ) : (
                    <div className="text-amber-600">
                      {uploading ? (
                        <Loader2 className="w-8 h-8 mx-auto animate-spin" />
                      ) : (
                        <>
                          <ImageIcon className="w-12 h-12 mx-auto mb-3 text-amber-500" />
                          <p>点击上传封面图片</p>
                          <p className="text-sm">推荐尺寸 1920x1080</p>
                        </>
                      )}
                    </div>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
              </div>

              {/* 展览标题 */}
              <div>
                <Label htmlFor="title">展览标题 *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="输入展览标题"
                  className="mt-1"
                  required
                />
              </div>

              {/* 展览描述 */}
              <div>
                <Label htmlFor="description">展览介绍</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="介绍展览的主题、内容、意义等..."
                  className="mt-1 min-h-32"
                />
              </div>

              {/* 主办方 */}
              <div>
                <Label htmlFor="organizer">主办方</Label>
                <Input
                  id="organizer"
                  value={formData.organizer}
                  onChange={(e) => setFormData({ ...formData, organizer: e.target.value })}
                  placeholder={user?.name || '输入主办方名称'}
                  className="mt-1"
                />
              </div>

              {/* 线上/线下 */}
              <div className="flex items-center justify-between">
                <div>
                  <Label>线上展览</Label>
                  <p className="text-sm text-amber-600">线上展览可以在网站上直接浏览</p>
                </div>
                <Switch
                  checked={formData.is_online}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_online: checked })}
                />
              </div>

              {/* 展览地点 */}
              {!formData.is_online && (
                <div>
                  <Label htmlFor="location">展览地点</Label>
                  <div className="relative mt-1">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-amber-500" />
                    <Input
                      id="location"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      placeholder="输入展览地址"
                      className="pl-10"
                    />
                  </div>
                </div>
              )}

              {/* 展览日期 */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="start_date">开始日期</Label>
                  <div className="relative mt-1">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-amber-500" />
                    <Input
                      id="start_date"
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                      className="pl-10"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="end_date">结束日期</Label>
                  <div className="relative mt-1">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-amber-500" />
                    <Input
                      id="end_date"
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                      className="pl-10"
                    />
                  </div>
                </div>
              </div>

              {/* 提交按钮 */}
              <div className="flex gap-4 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => navigate(-1)}
                  className="flex-1"
                >
                  取消
                </Button>
                <Button 
                  type="submit" 
                  disabled={loading}
                  className="flex-1"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      创建中...
                    </>
                  ) : (
                    '发布展览'
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CreateExhibitionPage;
